/**
 * Admin API Service for NGUI
 *
 * API client for admin-only endpoints (user management, audit logs)
 * All endpoints require admin authentication
 */

import apiClient from './api';
import type {
  User,
  UserCreateRequest,
  UserUpdateRequest,
  AuditLog,
  WebSocketEvent,
} from '@/types/user';

// ============================================================
// User Management API
// ============================================================

export interface PaginatedUsersResponse {
  users: User[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Get users with optional filters, pagination, and search
 * @param status Filter by user status (active, blocked, archived)
 * @param role Filter by user role (admin, user)
 * @param search Search by username, full_name, or email
 * @param limit Page size (default 50)
 * @param offset Start offset
 * @param tenantId Filter by tenant (for tenant admins or superadmin)
 * @returns Paginated users response
 * @throws Error if not authorized (401/403)
 */
export const listUsers = async (
  status?: string,
  role?: string,
  search?: string,
  limit?: number,
  offset?: number,
  tenantId?: string
): Promise<PaginatedUsersResponse> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (role) params.append('role', role);
  if (search) params.append('search', search);
  if (limit !== undefined) params.append('limit', limit.toString());
  if (offset !== undefined) params.append('offset', offset.toString());
  if (tenantId) params.append('tenant_id', tenantId);

  const response = await apiClient.get<PaginatedUsersResponse>('/admin/users', { params });
  return response.data;
};

/**
 * Create a new user
 * @param data User creation data
 * @returns The created user
 * @throws Error if username exists (400) or not authorized (401/403)
 */
export const createUser = async (data: UserCreateRequest): Promise<User> => {
  const response = await apiClient.post<User>('/admin/users', data);
  return response.data;
};

/**
 * Update an existing user
 * @param userId User ID to update
 * @param data Fields to update
 * @returns The updated user
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const updateUser = async (
  userId: number,
  data: UserUpdateRequest
): Promise<User> => {
  const response = await apiClient.put<User>(`/admin/users/${userId}`, data);
  return response.data;
};

/**
 * Block a user account
 * @param userId User ID to block
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const blockUser = async (userId: number): Promise<void> => {
  await apiClient.post(`/admin/users/${userId}/block`);
};

/**
 * Unblock a user account
 * @param userId User ID to unblock
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const unblockUser = async (userId: number): Promise<void> => {
  await apiClient.post(`/admin/users/${userId}/unblock`);
};

/**
 * Archive a user account (soft delete)
 * @param userId User ID to archive
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const archiveUser = async (userId: number): Promise<void> => {
  await apiClient.post(`/admin/users/${userId}/archive`);
};

/**
 * Permanently delete a user
 * @param userId User ID to delete
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const deleteUser = async (userId: number): Promise<void> => {
  await apiClient.delete(`/admin/users/${userId}`);
};

/**
 * Enable or disable avatar feature for a user (admin only).
 * Mirrors the voice-toggle response shape.
 */
export const toggleUserAvatar = async (
  userId: number,
  enabled: boolean
): Promise<{ success: boolean; avatar_enabled: boolean }> => {
  const response = await apiClient.put<{ success: boolean; avatar_enabled: boolean }>(
    `/admin/users/${userId}/avatar`,
    null,
    { params: { enabled } }
  );
  return response.data;
};

// ============================================================
// Audit Log API
// ============================================================

export interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface WebSocketEventsResponse {
  events: WebSocketEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditLogFilters {
  limit?: number;
  offset?: number;
  level?: string;
  username?: string;
  session_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface WebSocketEventFilters {
  limit?: number;
  offset?: number;
  session_id?: string;
  username?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * Get audit logs with comprehensive filtering and pagination
 * @param filters Filter and pagination parameters
 * @returns Audit logs with pagination metadata
 * @throws Error if not authorized (401/403)
 */
export const getAuditLogs = async (
  filters: AuditLogFilters = {}
): Promise<AuditLogsResponse> => {
  const params = new URLSearchParams();
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
  if (filters.level) params.append('level', filters.level);
  if (filters.username) params.append('username', filters.username);
  if (filters.session_id) params.append('session_id', filters.session_id);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);

  const response = await apiClient.get<AuditLogsResponse>('/admin/audit/logs', { params });
  return response.data;
};

/**
 * Get WebSocket events with filtering and pagination
 * @param filters Filter and pagination parameters
 * @returns WebSocket events with pagination metadata
 * @throws Error if not authorized (401/403)
 */
export const getWebSocketEvents = async (
  filters: WebSocketEventFilters = {}
): Promise<WebSocketEventsResponse> => {
  const params = new URLSearchParams();
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString());
  if (filters.session_id) params.append('session_id', filters.session_id);
  if (filters.username) params.append('username', filters.username);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);

  const response = await apiClient.get<WebSocketEventsResponse>('/admin/audit/websocket-events', { params });
  return response.data;
};

/**
 * Get list of unique usernames that have audit logs
 * @returns Array of usernames
 * @throws Error if not authorized (401/403)
 */
export const getAuditUsernames = async (): Promise<string[]> => {
  const response = await apiClient.get<{ usernames: string[] }>('/admin/audit/usernames');
  return response.data.usernames;
};

/**
 * Get list of unique session IDs, optionally filtered by username
 * @param username Optional username to filter sessions
 * @returns Array of session IDs
 * @throws Error if not authorized (401/403)
 */
export const getAuditSessions = async (username?: string): Promise<string[]> => {
  const params = new URLSearchParams();
  if (username) params.append('username', username);

  const response = await apiClient.get<{ sessions: string[] }>('/admin/audit/sessions', { params });
  return response.data.sessions;
};

// ============================================================
// Admin GDPR Management API
// ============================================================

export interface AdminDeletionRequest {
  id: string;
  user_id: number;
  username: string;
  email: string;
  full_name: string;
  status: 'pending' | 'cancelled' | 'executed';
  reason?: string;
  requested_at: string;
  scheduled_deletion_at: string;
  completed_at?: string;
  ip_address?: string;
}

export interface AdminDeletionRequestsResponse {
  requests: AdminDeletionRequest[];
  total: number;
  pending_count: number;
  cancelled_count: number;
  executed_count: number;
}

export interface AdminDataExport {
  id: string;
  user_id: number;
  username: string;
  email: string;
  status: 'pending' | 'processing' | 'ready' | 'failed' | 'expired';
  format: string;
  requested_at: string;
  completed_at?: string;
  expires_at?: string;
  file_size_bytes?: number;
  error_message?: string;
}

export interface AdminDataExportsResponse {
  exports: AdminDataExport[];
  total: number;
  pending_count: number;
  processing_count: number;
  ready_count: number;
  failed_count: number;
}

/**
 * Get all account deletion requests (admin only)
 * @param status Optional status filter
 * @returns List of deletion requests with counts
 */
export const getAdminDeletionRequests = async (
  status?: string
): Promise<AdminDeletionRequestsResponse> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);

  const response = await apiClient.get<AdminDeletionRequestsResponse>(
    '/admin/gdpr/deletion-requests',
    { params }
  );
  return response.data;
};

/**
 * Get details of a specific deletion request
 * @param requestId The deletion request ID
 * @returns Deletion request with user details
 */
export const getAdminDeletionRequestDetail = async (
  requestId: string
): Promise<{ request: AdminDeletionRequest; user: User | null }> => {
  const response = await apiClient.get(`/admin/gdpr/deletion-requests/${requestId}`);
  return response.data;
};

/**
 * Expedite account deletion (skip 30-day cooling-off period)
 * @param requestId The deletion request ID
 * @returns Confirmation message
 */
export const expediteDeletion = async (
  requestId: string
): Promise<{ message: string; request_id: string; status: string }> => {
  const response = await apiClient.post(`/admin/gdpr/deletion-requests/${requestId}/expedite`);
  return response.data;
};

/**
 * Get all data export requests (admin only)
 * @param status Optional status filter
 * @returns List of data exports with counts
 */
export const getAdminDataExports = async (
  status?: string
): Promise<AdminDataExportsResponse> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);

  const response = await apiClient.get<AdminDataExportsResponse>(
    '/admin/gdpr/data-exports',
    { params }
  );
  return response.data;
};

// ============================================================
// User Detail & Permissions API
// ============================================================

export interface UserSession {
  id: string;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  is_active: boolean;
}

export interface AIUsage {
  tasks_total: number;
  tasks_completed: number;
  tasks_in_progress: number;
  tasks_pending: number;
  tasks_archived: number;
  iterations_total: number;
  iterations_completed: number;
  files_total: number;
  folders_total: number;
  events_total: number;
  events_upcoming: number;
  kb_total: number;
  cogito_entities: number;
  cogito_insights: number;
  contacts_total: number;
  companies_total: number;
  last_activity_at: string | null;
}

export interface AppPermissions {
  calendar: boolean;
  email: boolean;
  knowledge_base: boolean;
  cogito: boolean;
  tasks: boolean;
  disk: boolean;
  people: boolean;
  chat: boolean;
  contractors: boolean;
}

export interface SupportTicket {
  id: number;
  subject: string;
  category: string;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface UserDetail {
  user: User & {
    voice_enabled: boolean;
    app_permissions: AppPermissions;
  };
  sessions: UserSession[];
  usage: AIUsage;
  tickets: SupportTicket[];
  ticket_count: number;
}

export interface UserAgentAssignment {
  assignment_id: number | null;
  agent_id: number;
  agent_name: string;
  agent_description: string | null;
  agent_status: string;
  enabled: boolean;
  limitation_prompt: string | null;
  priority: number | null;
}

export interface UserAgentUpdate {
  agent_id: number;
  enabled?: boolean;
  limitation_prompt?: string | null;
}

/**
 * Get comprehensive user details including sessions, usage metrics, and tickets
 * @param userId User ID
 * @returns UserDetail with user info, sessions, usage, and tickets
 */
export const getUserDetail = async (userId: number): Promise<UserDetail> => {
  const response = await apiClient.get<UserDetail>(`/admin/users/${userId}/detail`);
  return response.data;
};

/**
 * Update user's app-level permissions
 * @param userId User ID
 * @param permissions Partial permissions to update
 * @returns Updated permissions
 */
export const updateUserPermissions = async (
  userId: number,
  permissions: Partial<AppPermissions>
): Promise<{ success: boolean; permissions: AppPermissions }> => {
  const response = await apiClient.put(`/admin/users/${userId}/permissions`, permissions);
  return response.data;
};

/**
 * Toggle voice UI for a user
 * @param userId User ID
 * @param enabled Whether to enable or disable voice
 * @returns Updated voice_enabled status
 */
export const updateUserVoice = async (
  userId: number,
  enabled: boolean
): Promise<{ success: boolean; voice_enabled: boolean }> => {
  const response = await apiClient.put(`/admin/users/${userId}/voice`, null, {
    params: { enabled }
  });
  return response.data;
};

/**
 * Get all agent assignments for a user
 * @param userId User ID
 * @returns List of all agents with user's assignment status
 */
export const getUserAgents = async (userId: number): Promise<{ agents: UserAgentAssignment[]; user_id: number }> => {
  const response = await apiClient.get(`/admin/users/${userId}/agents`);
  return response.data;
};

/**
 * Update a user's agent assignment (toggle enabled or set limitation prompt)
 * @param userId User ID
 * @param update Agent update data
 * @returns Updated assignment
 */
export const updateUserAgent = async (
  userId: number,
  update: UserAgentUpdate
): Promise<{ success: boolean; assignment: UserAgentAssignment }> => {
  const response = await apiClient.put(`/admin/users/${userId}/agents`, update);
  return response.data;
};

/**
 * Update a user's preferred language (admin only)
 * @param userId User ID
 * @param language Language code ('en' or 'it')
 * @returns Success response
 */
export const updateUserPreferredLanguage = async (
  userId: number,
  language: string
): Promise<{ success: boolean }> => {
  const response = await apiClient.put(`/admin/users/${userId}/preferred-language`, {
    preferred_language: language,
  });
  return response.data;
};

// ============================================================
// Agents API
// ============================================================

export interface AgentStats {
  tasks_completed: number;
  tasks_failed: number;
  total_cost_usd: number;
  avg_completion_hours: number;
}

export interface Agent {
  id: number;
  name: string;
  description: string | null;
  status: 'active' | 'paused';
  stats: AgentStats;
  assigned_user_count: number;
  created_at: string;
  updated_at: string;
}

export interface AgentDetail extends Agent {
  system_prompt: string;
  assigned_users: Array<{
    id: number;
    username: string;
    full_name: string;
    enabled: boolean;
  }>;
}

/**
 * List all agents with summary info
 * @param status Optional status filter ('active' or 'paused')
 * @returns List of agents
 */
export const listAgents = async (status?: string): Promise<{ agents: Agent[]; total: number }> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  const response = await apiClient.get('/admin/agents', { params });
  return response.data;
};

/**
 * Get detailed agent info including system prompt and assigned users
 * @param agentId Agent ID
 * @returns Agent detail
 */
export const getAgent = async (agentId: number): Promise<AgentDetail> => {
  const response = await apiClient.get(`/admin/agents/${agentId}`);
  return response.data;
};

// ============================================================
// System Status Monitoring API
// ============================================================

export interface ComponentStatus {
  component_name: string;
  component_category: string;
  display_name: string;
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  last_check_at: string | null;
  response_time_ms: number | null;
  error_message: string | null;
  details: Record<string, unknown>;
  last_healthy_at: string | null;
  consecutive_failures: number;
  updated_at: string | null;
}

export interface StatusSummary {
  overall_status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  healthy_count: number;
  degraded_count: number;
  unhealthy_count: number;
  unknown_count: number;
  total_count: number;
  last_check_at: string | null;
  components: ComponentStatus[];
}

export interface AICreditsStatus {
  provider: string;
  credits_valid: boolean;
  model_tested: string | null;
  error_message: string | null;
  last_check_at: string | null;
  details: Record<string, unknown>;
}

export interface AICreditsResponse {
  providers: AICreditsStatus[];
}

export interface StatusCheckResult {
  timestamp: string;
  overall_status: string;
  healthy: number;
  degraded: number;
  unhealthy: number;
  total: number;
  message: string;
}

export interface StatusHistoryItem {
  id: string;
  check_timestamp: string;
  component_name: string;
  component_category: string;
  status: string;
  response_time_ms: number | null;
  error_message: string | null;
  details: Record<string, unknown>;
}

export interface StatusHistoryResponse {
  items: StatusHistoryItem[];
  count: number;
}

export interface StatusHistoryFilters {
  component?: string;
  category?: string;
  status?: string;
  hours?: number;
  limit?: number;
  offset?: number;
}

/**
 * Get current system status summary
 * @param category Optional category filter
 * @returns Status summary with component details
 * @throws Error if not authorized (401/403)
 */
export const getSystemStatus = async (
  category?: string
): Promise<StatusSummary> => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);

  const response = await apiClient.get<StatusSummary>('/admin/status', { params });
  return response.data;
};

/**
 * Get AI API credits status
 * @returns AI credits status for all providers
 * @throws Error if not authorized (401/403)
 */
export const getAICreditsStatus = async (): Promise<AICreditsResponse> => {
  const response = await apiClient.get<AICreditsResponse>('/admin/status/ai-credits');
  return response.data;
};

/**
 * Trigger an immediate status check of all components
 * @returns Result of the status check
 * @throws Error if not authorized (401/403)
 */
export const triggerStatusCheck = async (): Promise<StatusCheckResult> => {
  const response = await apiClient.post<StatusCheckResult>('/admin/status/check');
  return response.data;
};

/**
 * Get historical status checks
 * @param filters Filter and pagination parameters
 * @returns Historical status checks
 * @throws Error if not authorized (401/403)
 */
export const getStatusHistory = async (
  filters: StatusHistoryFilters = {}
): Promise<StatusHistoryResponse> => {
  const params = new URLSearchParams();
  if (filters.component) params.append('component', filters.component);
  if (filters.category) params.append('category', filters.category);
  if (filters.status) params.append('status', filters.status);
  if (filters.hours !== undefined) params.append('hours', filters.hours.toString());
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString());

  const response = await apiClient.get<StatusHistoryResponse>('/admin/status/history', { params });
  return response.data;
};

// ============================================================
// Password Management API
// ============================================================

/**
 * Reset a user's password (admin only)
 * @param userId User ID to reset password for
 * @param newPassword The new password
 * @returns Success message
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const resetUserPassword = async (
  userId: number,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(`/admin/users/${userId}/reset-password`, {
    new_password: newPassword,
  });
  return response.data;
};

// ============================================================
// User File Stats API
// ============================================================

export interface UserFileStats {
  total_files: number;
  total_size_bytes: number;
  document_count: number;
  document_size: number;
  image_count: number;
  image_size: number;
  other_count: number;
  other_size: number;
}

/**
 * Get file/disk usage statistics for a user (admin only)
 * @param userId User ID
 * @returns File statistics breakdown by type
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const getUserFileStats = async (userId: number): Promise<UserFileStats> => {
  const response = await apiClient.get<UserFileStats>(`/admin/users/${userId}/file-stats`);
  return response.data;
};

// ============================================================
// Tenant Management API (Superadmin Only)
// ============================================================

export interface Tenant {
  id: string;
  name: string;
  domain: string;
  status: string;
  user_count: number;
  created_at: string;
  updated_at: string;
  tenant_type?: 'standard' | 'institutional';
  institutional_user_id?: number;
}

export interface UserTenantMembership {
  tenant_id: string;
  tenant_name: string;
  role: string;
}

/**
 * List all tenants (superadmin only)
 * @returns List of all tenants with user counts
 * @throws Error if not superadmin (403)
 */
export const listTenants = async (): Promise<{ tenants: Tenant[] }> => {
  const response = await apiClient.get<{ tenants: Tenant[] }>('/admin/tenants');
  return response.data;
};

/**
 * Get user's tenant memberships
 * @param userId User ID
 * @returns List of tenants the user belongs to
 * @throws Error if user not found (404) or not authorized (401/403)
 */
export const getUserTenants = async (
  userId: number
): Promise<{ user_id: number; tenants: UserTenantMembership[] }> => {
  const response = await apiClient.get<{ user_id: number; tenants: UserTenantMembership[] }>(
    `/admin/users/${userId}/tenants`
  );
  return response.data;
};

/**
 * Set user's tenant memberships (superadmin only)
 * @param userId User ID
 * @param tenantIds List of tenant IDs to assign
 * @returns Updated tenant memberships
 * @throws Error if not superadmin (403) or user not found (404)
 */
export const setUserTenants = async (
  userId: number,
  tenantIds: string[]
): Promise<{ success: boolean; user_id: number; tenants: UserTenantMembership[] }> => {
  const response = await apiClient.put(`/admin/users/${userId}/tenants`, {
    tenant_ids: tenantIds,
  });
  return response.data;
};

/**
 * Add user to a specific tenant (superadmin)
 * @param tenantId Tenant ID
 * @param userId User ID
 * @param role Role in tenant ('user' or 'admin')
 */
export const addUserToTenant = async (
  tenantId: string,
  userId: number,
  role: string = 'user'
): Promise<{ status: string; tenant_id: string; user_id: number; role: string }> => {
  const response = await apiClient.post(`/admin/tenants/${tenantId}/users/${userId}`, { role });
  return response.data;
};

/**
 * Remove user from a specific tenant (superadmin)
 * @param tenantId Tenant ID
 * @param userId User ID
 */
export const removeUserFromTenant = async (
  tenantId: string,
  userId: number
): Promise<{ status: string }> => {
  const response = await apiClient.delete(`/admin/tenants/${tenantId}/users/${userId}`);
  return response.data;
};

// ============================================================
// Tenant Admin Scoped API
// ============================================================

/**
 * Get tenants where current user is admin
 * @returns List of tenants where caller has admin role
 */
export const getMyAdminTenants = async (): Promise<{ tenants: Array<{ tenant_id: string; name: string; domain: string; role: string }> }> => {
  const response = await apiClient.get('/admin/tenants/my-tenants');
  return response.data;
};

/**
 * Add user to tenant (tenant admin scoped)
 * @param userId User ID
 * @param role Role ('user' or 'admin')
 * @param tenantId Optional specific tenant ID (for multi-tenant admins)
 */
export const addUserToTenantScoped = async (
  userId: number,
  role: string = 'user',
  tenantId?: string
): Promise<{ status: string }> => {
  const params = tenantId ? { params: { tenant_id: tenantId } } : {};
  const response = await apiClient.post(`/admin/tenants/users/${userId}`, { role }, params);
  return response.data;
};

/**
 * Remove user from tenant (tenant admin scoped)
 * @param userId User ID
 * @param tenantId Optional specific tenant ID (for multi-tenant admins)
 */
export const removeUserFromTenantScoped = async (
  userId: number,
  tenantId?: string
): Promise<{ status: string }> => {
  const params = tenantId ? { params: { tenant_id: tenantId } } : {};
  const response = await apiClient.delete(`/admin/tenants/users/${userId}`, params);
  return response.data;
};

// ============================================================
// Tenant Features API (Superadmin Only)
// ============================================================

export interface TenantFeatures {
  features: Record<string, boolean>;
  tenant_id: string;
}

/**
 * Get tenant feature flags (superadmin only)
 * @param tenantId Tenant ID
 * @returns Feature flags for the tenant
 * @throws Error if not superadmin (403)
 */
export const getTenantFeatures = async (tenantId: string): Promise<TenantFeatures> => {
  const response = await apiClient.get<TenantFeatures>(`/admin/tenants/${tenantId}/features`);
  return response.data;
};

/**
 * Set a tenant feature flag (superadmin only)
 * @param tenantId Tenant ID
 * @param featureKey Feature key to set
 * @param enabled Whether to enable or disable the feature
 * @returns Updated feature status
 * @throws Error if not superadmin (403)
 */
export const setTenantFeature = async (
  tenantId: string,
  featureKey: string,
  enabled: boolean
): Promise<{ status: string; feature: string; enabled: boolean }> => {
  const response = await apiClient.put(`/admin/tenants/${tenantId}/features/${featureKey}`, { enabled });
  return response.data;
};

/**
 * Update user's role in tenant (tenant admin scoped)
 * @param userId User ID
 * @param role New role ('user' or 'admin')
 * @param tenantId Optional specific tenant ID (for multi-tenant admins)
 */
export const updateUserRoleScoped = async (
  userId: number,
  role: string,
  tenantId?: string
): Promise<{ status: string; role: string }> => {
  const params = tenantId ? { params: { tenant_id: tenantId } } : {};
  const response = await apiClient.put(`/admin/tenants/users/${userId}/role`, { role }, params);
  return response.data;
};

// ============================================================
// Dashboard Stats API
// ============================================================

export interface DashboardStats {
  users: { total: number; active: number; active_today: number; active_week: number; new_this_month: number };
  tasks: { total: number; completed: number; in_progress: number };
  files: { total: number };
  iterations: { completed: number };
  processing: { processing: number; ready: number; failed: number };
  costs: {
    total_ai_cost: number;
    ai_cost_30d: number;
    ai_cost_today: number;
    total_processing_cost: number;
    top_spenders: Array<{ id: number; username: string; full_name: string; total_cost: number }>;
  };
  activity_chart: Array<{
    day: string;
    unique_users: number;
    total_actions: number;
    logins: number;
    file_ops: number;
    task_ops: number;
    voice_sessions: number;
  }>;
}

export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await apiClient.get<DashboardStats>('/admin/dashboard-stats');
  return response.data;
};

// ============================================================
// User Costs API
// ============================================================

export interface UserCosts {
  total_ai_cost: number;
  ai_cost_30d: number;
  iterations_completed: number;
  avg_cost_per_iteration: number;
  total_processing_cost: number;
  files_processed: number;
}

export const getUserCosts = async (userId: number): Promise<UserCosts> => {
  const response = await apiClient.get<UserCosts>(`/admin/users/${userId}/costs`);
  return response.data;
};

// ============================================================
// Money Tab API
// ============================================================

export interface MoneyReportItem {
  user_id: number;
  username: string;
  full_name: string;
  ai_task_cost: number;
  ai_iterations_count: number;
  file_processing_cost: number;
  files_processed_count: number;
  total_cost: number;
}

export interface MoneyReportTotals {
  ai_task_cost: number;
  ai_iterations_count: number;
  file_processing_cost: number;
  files_processed_count: number;
  total_cost: number;
}

export interface MoneyReportResponse {
  items: MoneyReportItem[];
  totals: MoneyReportTotals;
  count: number;
}

export const getMoneyReport = async (filters: {
  tenant_id?: string;
  user_ids?: number[];
  date_from?: string;
  date_to?: string;
}): Promise<MoneyReportResponse> => {
  const params = new URLSearchParams();
  if (filters.tenant_id) params.append('tenant_id', filters.tenant_id);
  if (filters.user_ids && filters.user_ids.length > 0)
    params.append('user_ids', filters.user_ids.join(','));
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  const response = await apiClient.get<MoneyReportResponse>('/admin/money', { params });
  return response.data;
};

// ============================================================
// Roles & Permissions API
// ============================================================

import type { Role, RoleWithPermissions, Permission, RoleCreateRequest, RoleUpdateRequest } from '@/types/roles';

export const listRoles = async (): Promise<{ roles: Role[] }> => {
  const response = await apiClient.get<{ roles: Role[] }>('/admin/roles');
  return response.data;
};

export const getRole = async (roleId: number): Promise<RoleWithPermissions> => {
  const response = await apiClient.get<RoleWithPermissions>(`/admin/roles/${roleId}`);
  return response.data;
};

export const createRole = async (data: RoleCreateRequest): Promise<Role> => {
  const response = await apiClient.post<Role>('/admin/roles', data);
  return response.data;
};

export const updateRole = async (roleId: number, data: RoleUpdateRequest): Promise<Role> => {
  const response = await apiClient.put<Role>(`/admin/roles/${roleId}`, data);
  return response.data;
};

export const deleteRole = async (roleId: number): Promise<void> => {
  await apiClient.delete(`/admin/roles/${roleId}`);
};

export const listPermissions = async (): Promise<{ permissions: Permission[] }> => {
  const response = await apiClient.get<{ permissions: Permission[] }>('/admin/roles/permissions');
  return response.data;
};

export const setRolePermissions = async (
  roleId: number,
  permissionIds: number[]
): Promise<{ count: number }> => {
  const response = await apiClient.put<{ count: number }>(
    `/admin/roles/${roleId}/permissions`,
    { permission_ids: permissionIds }
  );
  return response.data;
};

export const setRoleTenants = async (
  roleId: number,
  tenantIds: string[]
): Promise<void> => {
  await apiClient.put(`/admin/roles/${roleId}/tenants`, { tenant_ids: tenantIds });
};

// ============================================================
// API Key Management
// ============================================================

export interface ApiKeyInfo {
  key_name: string;
  key_hint: string;
  is_valid: boolean | null;
  validated_at: string | null;
  updated_by: number | null;
  updated_at: string | null;
}

export interface ApiKeySetRequest {
  key_name: string;
  value: string;
  skip_validation?: boolean;
}

export interface ApiKeyValidateResponse {
  valid: boolean;
  error: string | null;
  model_tested: string | null;
}

export interface ApiKeyAuditEntry {
  id: number;
  key_name: string;
  action: string;
  key_hint: string;
  performed_by: number | null;
  performed_at: string;
  details: Record<string, unknown> | null;
}

export const listApiKeys = async (): Promise<{ keys: ApiKeyInfo[] }> => {
  const response = await apiClient.get<{ keys: ApiKeyInfo[] }>('/admin/keys');
  return response.data;
};

export const setApiKey = async (data: ApiKeySetRequest): Promise<ApiKeyInfo> => {
  const response = await apiClient.put<ApiKeyInfo>('/admin/keys', data);
  return response.data;
};

export const validateApiKey = async (data: { provider: string; value: string }): Promise<ApiKeyValidateResponse> => {
  const response = await apiClient.post<ApiKeyValidateResponse>('/admin/keys/validate', data);
  return response.data;
};

export const getApiKeyAudit = async (keyName?: string, limit?: number): Promise<{ entries: ApiKeyAuditEntry[] }> => {
  const params: Record<string, string> = {};
  if (keyName) params.key_name = keyName;
  if (limit) params.limit = String(limit);
  const response = await apiClient.get<{ entries: ApiKeyAuditEntry[] }>('/admin/keys/audit', { params });
  return response.data;
};
